import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import posthog from 'posthog-js';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(20);
  const [error, setError] = useState('');
  const { forgotPassword, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const emailValue = email.trim();
  const isFormValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid || loading) return;

    const maskedEmail = emailValue.replace(/(.{2}).+(@.*)/, '$1***$2');

    setLoading(true);
    setError('');
    console.groupCollapsed('[ForgotPassword] Password reset request');
    console.info('Payload', { email: maskedEmail });

    try {
      await forgotPassword(emailValue);
      posthog.capture('forgot_password_requested');
      console.info('Status', 'success');
      setSuccess(true);
      let seconds = 20;
      const interval = setInterval(() => {
        seconds -= 1;
        setRedirectCountdown(seconds);
        if (seconds <= 0) {
          clearInterval(interval);
          navigate('/login');
        }
      }, 1000);
    } catch (err) {
      console.error('Status', 'error', { message: err.message });
      setError(err.message || 'Erro ao enviar email. Tente novamente.');
    } finally {
      console.info('Request finished');
      console.groupEnd();
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-[#012B87] bg-fixed min-h-screen">
        <div className="max-w-[392px] mx-auto h-[90vh] flex items-center justify-center px-4 lg:px-0">
          <div className="w-full bg-gradient-to-br from-[#FBEEE8] to-[#E8F6FB] rounded px-5 py-7 sm:px-8 sm:py-9 md:px-10 md:py-11 text-center">
            <i className="bi-check-circle text-green-500 text-5xl mb-4"></i>
            <h1 className="text-lg md:text-2xl font-semibold mb-4">Email enviado!</h1>
            <p className="text-sm text-gray-600 mb-4">
              Verifique sua caixa de entrada para redefinir sua senha. Você será redirecionado em{' '}
              <span className="font-semibold text-slate-700">{redirectCountdown}s</span>.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-tBlue px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-tBlue2"
            >
              Voltar para o login
              <i className="bi-arrow-right-short text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#012B87] bg-fixed min-h-screen">
      <div className="max-w-[392px] mx-auto h-[90vh] flex items-center justify-center px-4 lg:px-0">
        <div className="w-full bg-gradient-to-br from-[#FBEEE8] to-[#E8F6FB] rounded px-5 py-7 sm:px-8 sm:py-9 md:px-10 md:py-11">
          <h1 className="text-lg md:text-2xl font-semibold mb-4">Esqueci a senha</h1>
          <p className="text-xs my-4 sm:my-5 text-gray-600">
            Digite seu email para receber instruções de recuperação de senha
          </p>

          {error && (
            <div className="bg-red-100 border border-red-200 text-red-600 text-xs rounded px-3 py-2 mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 text-slate-700 text-sm">
            <div
              className={`transition-all duration-500 border rounded flex items-center gap-3 sm:gap-4 py-2 px-4 h-[50px] ${
                emailValue ? 'border-[#2C1944]' : 'border-gray-300'
              }`}
            >
              <i className="bi-envelope transition-all duration-500 text-slate-400 text-lg sm:text-xl"></i>
              <div className="flex-1">
                <input
                  type="email"
                  id="emailInput"
                  className="w-full bg-transparent outline-none"
                  spellCheck="false"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <input
              type="submit"
              value={loading ? 'Enviando...' : 'Enviar'}
              className={`submitBtn transition-all w-full rounded py-2 cursor-pointer font-semibold mt-5 sm:mt-6 ${
                isFormValid && !loading
                  ? 'bg-tBlue hover:bg-tBlue2 text-white'
                  : 'bg-gradient-to-r from-[#EBEBEB] to-[#EBEBEB] text-slate-400 cursor-not-allowed'
              }`}
              disabled={!isFormValid || loading}
            />

            <div className="flex items-center gap-1 text-xs mt-2 justify-center">
              <Link to="/login" className="font-semibold text-xs sm:text-sm hover:underline flex items-center">
                <i className="bi-arrow-left-short text-tOrange text-xl mr-1"></i>
                Voltar para login
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
